import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import {
  RouterProvider,
  createBrowserRouter,
  redirect,
} from "react-router-dom";
import { resolveVanityLink } from "./api";
import "./global.css";
import i18n from "./i18n/config";
import "./index.css";
import DebugPage from "./pages/Debug";
import NotFoundPage from "./pages/NotFound";
import Root from "./pages/Root";
import Mailbox from "./pages/mailbox/Mailbox";
import DriveShareLinkPage from "./pages/share/ShareLink";
import PublishedSpacePage from "./pages/space/publishedSpace";
import SpaceInvitationPage from "./pages/space/spaceInvitation";
import TransferPage from "./pages/transfer/Transfer";
import { MuiThemeProvider } from "./providers/muiThemeProvider/muiThemeProvider";
import reportWebVitals from "./reportWebVitals";
import { MailboxGroupPage } from "./pages/mailboxGroup/mailboxGroup";

const router = createBrowserRouter([
  {
    path: "/",
    element: <DebugPage />,
  },
  {
    path: "/",
    element: <Root />,
    hasErrorBoundary: true,
    errorElement: <NotFoundPage />,
    children: [
      //==================================================================================================
      //                                    Legacy routes
      //==================================================================================================
      // mailbox route vanity, eg. -> {hostname}/fooBar
      {
        path: "/:maybeVanityName",
        loader: async ({ params }) => {
          const { maybeVanityName } = params;
          try {
            await resolveVanityLink(maybeVanityName!);
            // confirmed, that maybeVanityName is a vanity name
            return redirect(`/mailbox/${maybeVanityName}`);
          } catch (error) {
            // maybeVanityName is not a vanity name, render regular 404 page
            return undefined;
          }
        },
        element: <NotFoundPage />,
      },
      // transfer GET page route, eg. -> {hostname}/transfer/get/{recipientId}
      {
        path: "/my/transfers/:recipientId",
        element: <TransferPage />,
        loader: ({ params }) => {
          const { recipientId } = params;
          return redirect(`/transfer/get/${recipientId}`);
        },
      },
      //==================================================================================================
      //==================================================================================================
      //
      //
      //==================================================================================================
      //                                  Next Gen Routes
      //==================================================================================================
      {
        path: "/mailbox",
        children: [
          // mailbox route vanity, eg. -> {hostname}/mailbox/fooBar
          {
            path: ":vanityLink",
            element: <Mailbox />,
          },
          // mailbox group, eg -> {hostname}/mailbox/group/ProjectManagers
          {
            path: "team/:key",
            element: <MailboxGroupPage />,
          },
        ],
      },
      // transfer GET page route, eg. -> {hostname}/transfer/{recipientId}
      {
        path: "/transfer/get/:transferUid",
        element: <TransferPage />,
      },
      //==================================================================================================
      //==================================================================================================
      //
      // Shared drive files view(page) route, eg. -> {hostname}/share/files/{shareLinkUid}
      {
        path: "/share/files/:shareLinkUid",
        element: <DriveShareLinkPage />,
      },
      // Published-space viewer, eg. -> {hostname}/share/space/{spaceUid}
      {
        path: "/share/space/:spaceUid",
        element: <PublishedSpacePage />,
      },
      // Space invitation landing page, eg. -> {hostname}/invitation/{spaceUid}/{inviteeAddress}
      {
        path: "/invitation/:spaceUid/:invitee",
        element: <SpaceInvitationPage />,
      },
      //
      //
      // catch all error 404 page
      {
        path: "/*",
        element: <NotFoundPage />,
      },
    ],
  },
]);

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <MuiThemeProvider>
        <I18nextProvider i18n={i18n}>
          <RouterProvider router={router} />
        </I18nextProvider>
      </MuiThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
